/**
 * Statuses where an Expert Program is published and running: ACTIVE
 * (kick-off hasn't arrived yet) and LIVE (kick-off has arrived, program is
 * running). Mirrors the backend's PUBLISHED_STATUSES (app/models/program.py)
 * -- anywhere that used to check `status === "ACTIVE"` to mean "this program
 * is published" must check isPublishedProgramStatus(status) instead, so it
 * doesn't silently stop matching once the program transitions to LIVE.
 */
export function isPublishedProgramStatus(status: string | null | undefined): boolean {
  return status === "ACTIVE" || status === "LIVE";
}

/**
 * Whether a program is genuinely an unfinished draft — one the expert still has
 * to finish and publish.
 *
 * Exactly DRAFT, and nothing else. The draft banners used to derive this by
 * elimination (`!isPublishedProgramStatus(status) && status !== "COMPLETED"`),
 * which quietly swept in PENDING_REVIEW, REJECTED and ARCHIVED — so an account
 * with one submitted program and three archived ones was told it had "4
 * Programs draft in progress" with no drafts at all. Elimination also breaks
 * again every time a new status is added, which is why this is a positive test.
 *
 * The neighbouring statuses each need their own treatment, not this banner:
 * PENDING_REVIEW is awaiting a reviewer (the expert can't act on it), REJECTED
 * needs rework and re-submission, and ARCHIVED is finished with.
 */
export function isDraftProgramStatus(status: string | null | undefined): boolean {
  return status === "DRAFT";
}

/**
 * Whether the expert may still change an Expert Program's enrollment price.
 *
 * Same window as the rest of the program details: DRAFT, PENDING_REVIEW,
 * REJECTED, ACTIVE and LIVE. A new price only applies to future enrolments --
 * payments keep the price plan they were charged at, so nobody already
 * enrolled is affected. COMPLETED and ARCHIVED are closed.
 */
export function isProgramPriceEditable(status: string | null | undefined): boolean {
  return (
    status === "DRAFT" ||
    status === "PENDING_REVIEW" ||
    status === "REJECTED" ||
    status === "ACTIVE" ||
    status === "LIVE"
  );
}
